import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import './drawer.css';

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
}

export interface MenuProps {
  trigger: ReactNode;
  triggerLabel: string;
  items: MenuItem[];
}

/**
 * Accessible dropdown menu: button trigger with aria-expanded, arrow-key
 * navigation across items, Escape and outside-click to close, focus returned
 * to the trigger on close.
 */
export function Menu({ trigger, triggerLabel, items }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) {
      triggerRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        close(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    itemRefs.current[activeIndex]?.focus();
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, activeIndex, close]);

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % items.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + items.length) % items.length);
    }
  };

  return (
    <div ref={rootRef} className="ui-menu" onKeyDown={onMenuKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        className="ui-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        onClick={() => {
          setActiveIndex(0);
          setOpen((value) => !value);
        }}
      >
        {trigger}
      </button>
      {open ? (
        <div id={menuId} className="ui-menu-list" role="menu" aria-label={triggerLabel}>
          {items.map((item, index) => (
            <button
              key={item.label}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              type="button"
              role="menuitem"
              className={`ui-menu-item${item.danger ? ' ui-menu-item--danger' : ''}`}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={() => {
                close(true);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Content-rich dialogs (the task details panel) get a wider frame. */
  wide?: boolean;
}

/**
 * Modal dialog: labelled region, Escape to close, overlay click to close,
 * focus moved into the dialog on open and trapped with a simple Tab loop.
 * The element focused before opening is captured and focus is restored to it
 * when the dialog closes.
 */
export function Dialog({ open, title, onClose, children, footer, wide = false }: DialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  /**
   * The close handler, held by reference rather than by value.
   *
   * This used to be a dependency of the effect below, which made the effect
   * re-run on every render for any caller passing an inline arrow — and almost
   * every caller does. Re-running meant tearing down and rebuilding the focus
   * trap, and rebuilding it moves focus to the first control in the panel. So
   * typing a character into a controlled field re-rendered the dialog, which
   * threw focus onto the close button, and the second character went nowhere:
   * every text field in every dialog accepted exactly one keystroke at a time.
   *
   * The ref is updated during render on purpose. Doing it in an effect would
   * leave a stale handler installed for the first commit after a change, which
   * is precisely the window an Escape press lands in.
   */
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      return;
    }
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const focusables = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    focusables()[0]?.focus();

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key === 'Tab') {
        const elements = focusables();
        if (elements.length === 0) {
          return;
        }
        const first = elements[0];
        const last = elements[elements.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    };
    // Deliberately only `open`. See onCloseRef above: adding onClose back here
    // is what broke typing.
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="ui-dialog-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className={wide ? 'ui-dialog ui-dialog--wide' : 'ui-dialog'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="ui-dialog-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="ui-dialog-close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="ui-dialog-body">{children}</div>
        {footer ? <footer className="ui-dialog-footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export interface DrawerProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Optional contextual actions rendered as a "…" overflow menu (spec: destructive actions live here). */
  menuItems?: MenuItem[] | undefined;
  /** Content-rich drawers (CSV import, the copy-set editor) get a wider frame. */
  wide?: boolean;
  /**
   * Half the canvas. The lead drawer, which holds a contact card, a timeline
   * and an email compose side by side over a session. Kyu: "it's better if it
   * takes 50% of the screen real estate."
   */
  half?: boolean;
}

/**
 * Non-modal inspector drawer (redesign spec §1). Slides over the right edge
 * with NO backdrop: the page behind stays interactive, so selecting another
 * row simply re-renders the drawer in place. Escape closes it only while
 * focus is inside, and there is deliberately no focus trap.
 */
export function Drawer({
  open,
  title,
  onClose,
  children,
  footer,
  menuItems,
  wide = false,
  half = false,
}: DrawerProps) {
  const titleId = useId();

  if (!open) {
    return null;
  }

  return (
    <aside
      className={`ui-drawer${wide ? ' ui-drawer--wide' : ''}${half ? ' ui-drawer--half' : ''}`}
      role="complementary"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <header className="ui-drawer-header">
        <h2 id={titleId}>{title}</h2>
        {menuItems !== undefined && menuItems.length > 0 ? (
          <Menu
            trigger={<span aria-hidden>…</span>}
            triggerLabel="More actions"
            items={menuItems}
          />
        ) : null}
        <button
          type="button"
          className="ui-dialog-close"
          aria-label="Close panel"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <div className="ui-drawer-body">{children}</div>
      {footer !== undefined ? <footer className="ui-drawer-footer">{footer}</footer> : null}
    </aside>
  );
}
